import React, { useEffect } from 'react';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';

import { featureToggleChanged } from '@/configuration/featureToggle/featureToggleSlice';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { RootState } from '@/configuration/setup/store';
import { accessToken } from '@/configuration/tokenHandling/accessToken';
import { isReadinessNotifiedSelector, readinessNotified } from '@/features/app/appSlice';

// Events sent
export const OUT_NOTIFY_READINESS = 'MFE/BOOTSTRAP/MFE_IS_LOADED';

// Events received from web-workshop
export const TOKEN_RENEWED = 'WEB_WORKSHOP/TOKEN_RENEWED';

// Events sent to parent frontend
export const CLOSE_BUTTON = 'MAINTENANCE_HISTORY/CLOSE_BUTTON';
export const IN_MSG_FEATURE_TOGGLE_CHANGED = 'WEB_WORKSHOP/FEATURE_TOGGLE_CHANGED';
export const IN_MSG_CFE_FEATURE_TOGGLE_CHANGED = 'CFE/FEATURE_TOGGLE_CHANGED';

interface MessageEvent<T> {
    type: string;
    payload: T;
}

export const sendMessage = (message: MessageEvent<any>) => {
    const referrer = document.referrer;
    if (referrer) {
        window.parent.postMessage(message, referrer);
    }
};

export const receiveMessage = (event: any, dispatchRemoteAction: ThunkDispatch<Dispatch<any>, RootState, any>) => {
    const { type, payload } = event.data;
    if (!type) {
        return;
    }
    switch (type) {
        case TOKEN_RENEWED:
            const { token } = event.data.payload;
            accessToken.saveAccessToken(token);
            break;
        case IN_MSG_FEATURE_TOGGLE_CHANGED:
        case IN_MSG_CFE_FEATURE_TOGGLE_CHANGED:
            dispatchRemoteAction(
                featureToggleChanged({
                    featureKey: payload.key,
                    value: payload.enabled,
                }),
            );
            break;
        default:
            // eslint-disable-next-line no-console
            console.log('Received event in maintenance-history with type not handled: ', type);
            break;
    }
};

// eslint-disable-next-line react/display-name
const MessageHandler = React.memo(() => {
    const dispatch = useAppDispatch();
    const isReadinessNotified = useAppSelector(isReadinessNotifiedSelector);
    useEffect(() => {
        const listenerCallback = (event: any) => receiveMessage(event, dispatch);
        window.addEventListener('message', listenerCallback, false);

        if (!isReadinessNotified) {
            sendMessage({
                type: OUT_NOTIFY_READINESS,
                payload: {
                    mfeName: 'maintenance-history-service',
                },
            });

            dispatch(readinessNotified());
        }

        return () => window.removeEventListener('message', listenerCallback, false);
    }, []);

    return null;
});

export default MessageHandler;
